import React, { Component, ErrorInfo, ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState, useCallback } from "react";
import posthog from "posthog-js";

/* ────────────────────────────────────────────────────
   Lavender Design System Styles
   ──────────────────────────────────────────────────── */
const lavenderStyles = `
  .idea-detail-lavender {
    background:
      radial-gradient(ellipse 90% 70% at 5% -5%, rgba(175, 158, 252, 0.6) 0%, transparent 55%),
      radial-gradient(ellipse 70% 60% at 95% 8%, rgba(218, 148, 238, 0.45) 0%, transparent 52%),
      #ede9f5;
  }
  .frosted-card {
    background: rgba(255, 255, 255, 0.72);
    border: 1px solid rgba(255, 255, 255, 0.85);
  }
  .frosted-sidebar {
    background: rgba(255, 255, 255, 0.72);
    border: 1px solid rgba(255, 255, 255, 0.85);
    border-radius: 20px;
  }
  .font-instrument {
    font-family: 'Instrument Serif', serif;
  }
  .lavender-badge {
    background: rgba(124, 106, 240, 0.1);
    color: #7c6af0;
  }
  .lavender-btn-primary {
    background: #18151f;
    color: white;
    border-radius: 999px;
  }
  .lavender-btn-primary:hover {
    background: #2a2635;
  }
  .lavender-btn-ghost {
    background: rgba(124, 106, 240, 0.1);
    color: #7c6af0;
    border-radius: 999px;
  }
  .lavender-btn-ghost:hover {
    background: rgba(124, 106, 240, 0.18);
  }
`;

/** Truncate text to ~200 words with a Read more toggle */
function TruncatedText({ text, maxWords = 200 }: { text: string; maxWords?: number }) {
  const [expanded, setExpanded] = useState(false);
  const words = text.split(/\s+/);
  if (words.length <= maxWords) {
    return <p className="text-[#3d3556] leading-relaxed">{text}</p>;
  }
  const truncated = words.slice(0, maxWords).join(' ') + '...';
  return (
    <div>
      <p className="text-[#3d3556] leading-relaxed">{expanded ? text : truncated}</p>
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-xs text-[#7c6af0] hover:underline mt-1 font-medium"
      >
        {expanded ? 'Show less' : 'Read more'}
      </button>
    </div>
  );
}

class IdeaDetailErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error('IdeaDetail crash:', error, info); }
  render() {
    if (this.state.error) {
      return (
        <div className="p-8 max-w-2xl mx-auto mt-20">
          <h2 className="text-xl font-bold text-red-600 mb-2">Something went wrong</h2>
          <pre className="text-sm text-red-500 whitespace-pre-wrap bg-red-50 p-4 rounded-lg border border-red-200 overflow-auto">{this.state.error.message}</pre>
          <pre className="text-xs text-[#a1a1aa] whitespace-pre-wrap mt-2 max-h-40 overflow-auto">{this.state.error.stack}</pre>
          <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-primary text-white rounded-lg text-sm">Reload Page</button>
        </div>
      );
    }
    return this.props.children;
  }
}
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useFavorites, useIsFavorited } from "@/hooks/useFavorites";
import { useIdeaCache } from "@/contexts/IdeaCacheContext";
import { IdeaDetailSkeleton } from "@/components/skeletons/IdeaDetailSkeleton";

import { SEOHead } from "@/components/SEOHead";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tooltip as ShadcnTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Rocket,
  Download,
  TrendingUp,
  Target,
  Lightbulb,
  DollarSign,
  Clock,
  CheckCircle2,
  BarChart3,
  Users,
  Heart,
  Zap,
  AlertTriangle,
  Search,
  MessageSquare,
  Star,
  FileText,
  Youtube,
  Globe,
  Copy,
  Check,
  Share2,
  Bookmark,
  ArrowRight,
  Shield,
  Megaphone,
  Mail,
  PresentationIcon,
  CalendarDays,
} from "lucide-react";
import { analytics, trackEvent } from "@/lib/analytics";
import { Skeleton } from "@/components/ui/skeleton";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

// Simple markdown-to-JSX renderer for business plan content
// Simple text renderer - content is pre-formatted as plain text
function renderText(text: string): React.ReactNode {
  if (!text) return null;
  return <p className="text-[#3d3556]">{text}</p>;
}

// Canonical StartupDetail data model interface
interface FinancialAssumptions {
  target_customers_estimate: number;
  expected_penetration_rate: number; // as decimal, e.g., 0.02 = 2%
  avg_monthly_price: number;
  computed_arr: number;
  explanation: string;
}

interface StartupDetail {
  hero: {
    title: string;
    tagline: string;
    tags: string[];
  };
  executive_summary: string;
  topMetrics: {
    est_arr: number | null;
    market_size: number | null;
    pricing_summary: string;
    opportunity_score: number;
  };
  secondary: {
    revenue_potential_score: number;
    feasibility_score: number;
    key_risks_summary: string;
  };
  sections: {
    problem: string;
    solution: string;
    business_model: string;
    who_its_for: string;
    why_now: string;
    market_gap: string;
    proof_signals: string;
    how_to_build: string;
    pricing_value_ladder: Array<{ name: string; price: string; description: string }>;
  };
  charts: {
    search_growth_timeseries: Array<{ month: string; value: number }>;
    avg_volume: number;
    cagr_percent: number;
  };
  fit_scores: {
    revenue_potential: number;
    opportunity: number;
    problem_severity: number;
    feasibility: number;
    market_timing: number;
    overall_potential: number;
    solution_strength: number;
  };
  community_signals: {
    reddit_score: number;
    youtube_score: number;
    facebook_score: number;
    reddit_notes: string;
    youtube_notes: string;
    facebook_notes: string;
  };
  financial_assumptions: FinancialAssumptions;
}

// Generate canonical startup detail from idea data
// Compute unique financial assumptions based on idea characteristics
function computeFinancialAssumptions(idea: any): FinancialAssumptions {
  // Use idea's unique identifiers to seed deterministic but varied values
  const ideaHash = (idea.id || idea.title || '').split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
  const nicheHash = (idea.niche || '').split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
  
  // Estimate target customers based on market size or niche characteristics
  const marketSize = idea.market_size_usd || (1000000 + (ideaHash % 50) * 100000);
  
  // Determine pricing tier based on niche and business model
  const isB2B = (idea.icp || idea.target_customer || '').toLowerCase().includes('business') || 
                (idea.niche || '').toLowerCase().includes('enterprise') ||
                (idea.pricing_model || '').toLowerCase().includes('b2b');
  
  const isEnterprise = (idea.niche || '').toLowerCase().includes('enterprise') ||
                       (idea.target_customer || '').toLowerCase().includes('large');
  
  // Calculate avg monthly price based on business model
  let avgMonthlyPrice: number;
  if (isEnterprise) {
    avgMonthlyPrice = 199 + (nicheHash % 300); // $199-$499/mo
  } else if (isB2B) {
    avgMonthlyPrice = 49 + (nicheHash % 100); // $49-$149/mo
  } else {
    avgMonthlyPrice = 9 + (nicheHash % 40); // $9-$49/mo (B2C)
  }
  
  // Estimate addressable customers from market size (market / avg annual spend)
  const avgAnnualSpend = avgMonthlyPrice * 12;
  const totalAddressableCustomers = Math.round(marketSize / avgAnnualSpend);
  
  // Target customer estimate: realistic subset of TAM (10-30% based on opportunity)
  const opportunityFactor = (idea.opportunity_score || 70) / 100;
  const targetCustomersEstimate = Math.round(totalAddressableCustomers * (0.1 + opportunityFactor * 0.2));
  
  // Conservative penetration rate (0.5% - 3% based on feasibility)
  const feasibilityFactor = (idea.feasibility_score || 70) / 100;
  const penetrationRate = 0.005 + (feasibilityFactor * 0.025);
  
  // Compute ARR: customers * penetration * monthly price * 12
  const expectedCustomers = Math.round(targetCustomersEstimate * penetrationRate);
  const computedArr = Math.round(expectedCustomers * avgMonthlyPrice * 12);
  
  const explanation = `Based on ${targetCustomersEstimate.toLocaleString()} target customers at ${(penetrationRate * 100).toFixed(1)}% penetration rate, with avg. pricing of $${avgMonthlyPrice}/mo.`;
  
  return {
    target_customers_estimate: targetCustomersEstimate,
    expected_penetration_rate: penetrationRate,
    avg_monthly_price: avgMonthlyPrice,
    computed_arr: computedArr,
    explanation
  };
}

// ============================================================================
// STRICT CONTENT TEMPLATE ENFORCER
// Rules: 1 paragraph, 4-6 sentences, 90-130 words, no markdown, plain text only
// Structure: (1) what section is about, (2-3) details, (4-5) differentiation, (6) outcome
// ============================================================================

const MIN_WORDS = 90;
const MAX_WORDS = 130;
const MIN_SENTENCES = 4;
const MAX_SENTENCES = 6;

function countWords(text: string): number {
  return text.split(/\s+/).filter(w => w.length > 0).length;
}

function extractSentences(text: string): string[] {
  // Match sentences ending in . ! or ?
  const matches = text.match(/[^.!?]*[.!?]+/g) || [];
  return matches.map(s => s.trim()).filter(s => s.length > 5);
}

function stripMarkdown(text: string): string {
  return text
    .replace(/^#+\s+.*/gm, '')           // Remove headings
    .replace(/\*\*([^*]+)\*\*/g, '$1')   // Remove bold
    .replace(/\*([^*]+)\*/g, '$1')       // Remove italic
    .replace(/_([^_]+)_/g, '$1')         // Remove underline
    .replace(/`([^`]+)`/g, '$1')         // Remove code
    .replace(/^[A-Z][^:\n]+:\s*/gm, '')  // Remove labels like "Problem:"
    .replace(/^[-•*]\s*/gm, '')          // Remove bullets
    .replace(/^\d+\.\s*/gm, '')          // Remove numbered lists
    .replace(/["""'']/g, '"')            // Normalize quotes
    .replace(/\n+/g, ' ')                // Single line
    .replace(/\s+/g, ' ')                // Collapse whitespace
    .trim();
}

// STRICT TEMPLATE ENFORCER - applies hard rules before save/render
function formatSection(text: string): string {
  if (!text) return '';
  
  // 1. Strip all markdown/formatting
  const cleaned = stripMarkdown(text);
  
  // 2. Extract sentences
  let sentences = extractSentences(cleaned);
  
  // 3. Enforce MAX 6 sentences
  if (sentences.length > MAX_SENTENCES) {
    sentences = sentences.slice(0, MAX_SENTENCES);
  }
  
  // 4. Build result and check word count
  let result = sentences.join(' ');
  let wordCount = countWords(result);
  
  // 5. If over MAX_WORDS, remove sentences from end until under limit
  while (wordCount > MAX_WORDS && sentences.length > MIN_SENTENCES) {
    sentences.pop();
    result = sentences.join(' ');
    wordCount = countWords(result);
  }
  
  // 6. If still over MAX_WORDS after minimum sentences, truncate at word boundary
  if (wordCount > MAX_WORDS) {
    const words = result.split(/\s+/);
    result = words.slice(0, MAX_WORDS).join(' ');
    // Ensure ends with proper punctuation
    if (!result.match(/[.!?]$/)) {
      result = result.replace(/[,;:\s]+$/, '') + '.';
    }
  }
  
  // 7. Final check: ensure ends with punctuation
  if (result && !result.match(/[.!?]$/)) {
    result = result.replace(/[,;:\s]+$/, '') + '.';
  }
  
  return result;
}

// Helper to sanitize all section content and ensure executive_summary exists
function sanitizeSections(d: StartupDetail, idea: any): StartupDetail {
  const financialAssumptions = d.financial_assumptions || computeFinancialAssumptions(idea);
  const generatedContent = generateBusinessPlanContent(idea, financialAssumptions);
  
  // Always regenerate executive_summary with strict template
  const icp = idea?.icp || idea?.target_customer || 'professionals';
  const primaryNiche = (idea?.niche || 'technology').toLowerCase().split(/[\s,]+/).filter((t: string) => t.length > 2)[0] || 'workflow';
  const isB2B = icp.toLowerCase().includes('business') || (idea?.niche || '').toLowerCase().includes('enterprise');
  const avgPrice = financialAssumptions.avg_monthly_price || 29;
  const computedArr = financialAssumptions.computed_arr || 50000;
  const ideaHash = (idea?.id || idea?.title || '').split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
  
  // Executive summary: 4-6 sentences, 90-130 words, strict template
  const executiveSummary = formatSection(
    `${idea?.title || 'This startup'} addresses a critical gap in the ${idea?.niche || 'technology'} market where ${icp} struggle with fragmented ${primaryNiche} workflows. ` +
    `The platform consolidates multiple disconnected tools into a unified solution that saves time, reduces errors, and delivers measurable ROI through automation. ` +
    `With an estimated ARR potential of $${Math.round(computedArr / 1000)}K based on realistic market penetration, the business model supports ${isB2B ? 'B2B SaaS' : 'subscription'} pricing starting at $${avgPrice}/month. ` +
    `Market validation shows ${15 + (ideaHash % 20)}% annual search growth, active community discussions, and recent funding in adjacent spaces. ` +
    `The timing is favorable as infrastructure costs decline and buyer behavior shifts toward product-led discovery. ` +
    `This combination of proven demand, clear monetization, and accessible market entry creates a compelling opportunity for first-mover advantage.`
  );
  
  return {
    ...d,
    executive_summary: executiveSummary,
    sections: {
      ...d.sections,
      problem: generatedContent.problem,
      solution: generatedContent.solution,
      business_model: generatedContent.businessModel,
      who_its_for: generatedContent.whoItsFor,
      why_now: generatedContent.whyNow,
      market_gap: generatedContent.marketGap,
      proof_signals: generatedContent.proofSignals,
      how_to_build: generatedContent.howToBuild,
    }
  };
}

// ============================================================================
// TEMPLATED CONTENT GENERATOR
// Each section: 4-6 sentences, 90-130 words, product-explainer tone
// Structure: (1) what it's about, (2-3) details, (4-5) differentiation, (6) outcome
// ============================================================================

function generateBusinessPlanContent(idea: any, financialAssumptions: FinancialAssumptions) {
  const niche = idea.niche || 'technology';
  const title = idea.title || 'Startup';
  const icp = idea.icp || idea.target_customer || 'professionals';
  const primaryNiche = niche.toLowerCase().split(/[\s,]+/).filter((t: string) => t.length > 2)[0] || 'workflow';
  
  const ideaHash = (idea.id || '').split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
  const titleHash = title.split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
  const isB2B = icp.toLowerCase().includes('business') || niche.toLowerCase().includes('enterprise');
  const monthlyPrice = financialAssumptions.avg_monthly_price;

  // THE PROBLEM - 4-6 sentences, 90-130 words
  const problemContent = formatSection(
    `This section explains the core problem that ${title} solves for ${icp}. ` +
    `These users currently manage ${primaryNiche} workflows across ${4 + (ideaHash % 3)} disconnected tools, spending ${7 + (ideaHash % 10)}+ hours weekly on manual data transfer and reconciliation. ` +
    `Enterprise alternatives cost $${40 + (ideaHash % 40)}K+ annually, pricing out smaller organizations that still need professional-grade solutions. ` +
    `The fragmentation creates data silos, version conflicts, and ${55 + (ideaHash % 20)}% tool abandonment within the first 90 days of adoption. ` +
    `Existing point solutions fail to address workflow continuity, forcing users into inefficient workarounds that compound over time. ` +
    `This gap represents a $${2 + (ideaHash % 8)}B+ market opportunity waiting for a purpose-built platform.`
  );

  // THE SOLUTION - 4-6 sentences, 90-130 words
  const solutionContent = formatSection(
    `${title} consolidates fragmented ${primaryNiche} tools into a single integrated platform designed for ${icp}. ` +
    `Users complete onboarding in under ${5 + (titleHash % 5)} minutes, compared to the ${30 + (ideaHash % 20)}-minute industry average, with immediate access to core functionality. ` +
    `The platform automates ${60 + (ideaHash % 25)}% of routine tasks that previously required manual intervention, freeing users to focus on high-value work. ` +
    `Built-in integrations eliminate the need for third-party connectors while native collaboration features keep teams synchronized without external dependencies. ` +
    `Early adopters report ${35 + (ideaHash % 25)}% productivity gains within the first month of use. ` +
    `This approach eliminates context-switching costs while maintaining the power and flexibility users expect from professional tools.`
  );

  // BUSINESS MODEL - 4-6 sentences, 90-130 words
  const businessModelContent = formatSection(
    `Revenue comes from tiered SaaS subscriptions optimized for different customer segments and use cases. ` +
    `Starter plans at $${monthlyPrice}/mo serve individual users, Pro at $${Math.round(monthlyPrice * 2.5)}/mo targets growing teams, and Enterprise pricing covers custom deployments. ` +
    `The model achieves a 3.8x LTV:CAC ratio with ${Math.round((monthlyPrice * (isB2B ? 28 : 16) / 3.8) / monthlyPrice)}-month customer payback, supported by ${isB2B ? 'account expansion' : 'referral bonuses'}. ` +
    `Gross margins reach ${70 + (ideaHash % 20)}% after infrastructure costs, improving further with scale as fixed costs amortize across the customer base. ` +
    `Break-even requires approximately ${350 + (ideaHash % 250)} paying customers at current unit economics. ` +
    `Annual billing options improve cash flow predictability and boost retention by locking in long-term commitments.`
  );

  // WHO THIS IS FOR - 4-6 sentences, 90-130 words
  const whoItsForContent = formatSection(
    `The primary audience is ${isB2B ? 'operations leads at 15-200 person companies' : 'freelancers and agency owners'} spending $${180 + (titleHash % 300)}/mo on fragmented ${primaryNiche} tools. ` +
    `These buyers prioritize speed and simplicity over feature depth, making decisions quickly when ROI is demonstrable within a trial period. ` +
    `They typically discover solutions through peer recommendations, online communities, and product comparison sites rather than sales outreach. ` +
    `Secondary targets include teams outgrowing spreadsheet-based workflows but not yet ready for enterprise platform complexity or pricing. ` +
    `The ideal customer actively researches alternatives to their current fragmented setup and has budget authority for mid-market software. ` +
    `This profile represents ${(financialAssumptions.target_customers_estimate || 5000).toLocaleString()} addressable organizations in the primary market.`
  );

  // WHY NOW - 4-6 sentences, 90-130 words
  const whyNowContent = formatSection(
    `Several converging market forces make this the optimal entry window for ${title}. ` +
    `Infrastructure costs have dropped ${40 + (titleHash % 20)}% since 2021, enabling sophisticated features at price points accessible to SMB buyers. ` +
    `${70 + (ideaHash % 20)}% of buyers now complete research before contacting vendors, favoring product-led growth models that emphasize self-service trials. ` +
    `Legacy competitors carry substantial technical debt requiring multi-year rebuilds, creating a window for modern architectures to capture switching customers. ` +
    `Category advertising costs remain ${40 + (ideaHash % 30)}% below saturated adjacent markets, allowing efficient customer acquisition. ` +
    `Remote work has permanently accelerated demand for integrated digital workflows that function seamlessly across distributed teams.`
  );

  // MARKET GAP - 4-6 sentences, 90-130 words
  const marketGapContent = formatSection(
    `Current market solutions leave a significant gap between enterprise platforms and lightweight point solutions. ` +
    `Enterprise tools require $${40 + (ideaHash % 50)}K+ implementation budgets with 6-12 month deployment timelines, excluding the SMB segment entirely. ` +
    `Horizontal platforms like Notion and Airtable require extensive customization and lack domain-specific features for ${primaryNiche} workflows. ` +
    `Existing point solutions excel individually but create integration headaches when combined, forcing users to build custom bridges. ` +
    `Competitor NPS scores average 3.${6 + (ideaHash % 3)}/5 with recurring complaints about complexity, poor support, and feature bloat. ` +
    `The white space exists for a purpose-built solution that balances simplicity with comprehensive functionality at accessible pricing.`
  );

  // MARKET PROOF & SIGNALS - 4-6 sentences, 90-130 words
  const proofSignalsContent = formatSection(
    `Multiple data points validate strong market demand for ${title}'s positioning. ` +
    `Search volume shows ${40 + (ideaHash % 50)}K monthly queries for related terms with ${14 + (titleHash % 16)}% year-over-year growth, indicating expanding buyer awareness. ` +
    `Adjacent players raised $${200 + (ideaHash % 150)}M+ at 7-8x revenue multiples in recent funding rounds, confirming investor appetite for this category. ` +
    `Reddit communities surface weekly complaint threads about existing ${primaryNiche} tools, with users explicitly requesting the features ${title} provides. ` +
    `G2 and Capterra reviews for competitors average 3.${6 + (ideaHash % 3)}/5, with negative themes directly matching ${title}'s differentiation points. ` +
    `These signals indicate strong latent demand and favorable conditions for a well-positioned new entrant.`
  );

  // HOW TO BUILD - 4-6 sentences, 90-130 words
  const howToBuildContent = formatSection(
    `The build roadmap follows a phased approach optimized for speed to first revenue. ` +
    `Weeks 1-4 focus on ${15 + (ideaHash % 10)} user interviews and an MVP covering the 3 core workflows that represent ${80 + (ideaHash % 15)}% of daily user tasks. ` +
    `Weeks 5-8 launch private beta with ${20 + (ideaHash % 20)} users, targeting ${55 + (titleHash % 20)}% weekly activation rates to validate product-market fit. ` +
    `Weeks 9-12 add billing infrastructure, polish onboarding flows, and prepare for ProductHunt launch to generate initial traction. ` +
    `Months 4-6 expand to Pro tier features and target ${50 + (ideaHash % 50)} paying customers to establish baseline revenue. ` +
    `Year one goal: reach $${Math.round(financialAssumptions.computed_arr * 0.12 / 12).toLocaleString()} MRR with documented playbook for scaling.`
  );

  const targetCac = (monthlyPrice * (isB2B ? 28 : 16)) / 3.8;
  const cacChannels = isB2B 
    ? [
        { channel: 'LinkedIn Ads', cac: Math.round(targetCac * 1.2), conversion: (1.8 + (ideaHash % 12) / 10).toFixed(1), payback: Math.round((targetCac * 1.2) / monthlyPrice) },
        { channel: 'Google Ads', cac: Math.round(targetCac * 0.75), conversion: (4.0 + (titleHash % 15) / 10).toFixed(1), payback: Math.round((targetCac * 0.75) / monthlyPrice) },
        { channel: 'Content SEO', cac: Math.round(targetCac * 0.35), conversion: (3.2 + (titleHash % 10) / 10).toFixed(1), payback: Math.round((targetCac * 0.35) / monthlyPrice) }
      ]
    : [
        { channel: 'Meta Ads', cac: Math.round(targetCac * 1.1), conversion: (1.5 + (ideaHash % 10) / 10).toFixed(1), payback: Math.round((targetCac * 1.1) / monthlyPrice) },
        { channel: 'Organic SEO', cac: Math.round(targetCac * 0.28), conversion: (3.0 + (ideaHash % 12) / 10).toFixed(1), payback: Math.round((targetCac * 0.28) / monthlyPrice) },
        { channel: 'Referral', cac: Math.round(targetCac * 0.4), conversion: (8.0 + (titleHash % 18) / 10).toFixed(1), payback: Math.round((targetCac * 0.4) / monthlyPrice) }
      ];

  return {
    problem: problemContent,
    solution: solutionContent,
    businessModel: businessModelContent,
    whoItsFor: whoItsForContent,
    whyNow: whyNowContent,
    marketGap: marketGapContent,
    proofSignals: proofSignalsContent,
    howToBuild: howToBuildContent,
    cacChannels
  };
}

function generateStartupDetail(idea: any): StartupDetail {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // Use idea characteristics for deterministic but varied timeseries
  const ideaHash = (idea.id || idea.title || '').split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
  const baseValue = 35 + (ideaHash % 30);
  const growthRate = 2 + (ideaHash % 5);
  
  const timeseries = months.map((month, i) => ({
    month,
    value: Math.round(baseValue + (i * growthRate) + ((ideaHash + i) % 8))
  }));
  
  const avgVolume = Math.round(timeseries.reduce((sum, d) => sum + d.value, 0) / timeseries.length);
  const cagrPercent = Math.round(((timeseries[11].value - timeseries[0].value) / timeseries[0].value) * 100);
  
  const opportunityScore = idea.opportunity_score || (60 + (ideaHash % 30));
  const feasibilityScore = idea.feasibility_score || (55 + (ideaHash % 35));
  const revenueScore = idea.revenue_potential_score || (60 + (ideaHash % 30));

  // Compute financial assumptions
  const financialAssumptions = computeFinancialAssumptions(idea);
  
  // Generate rich business plan content
  const content = generateBusinessPlanContent(idea, financialAssumptions);
  
  // Use computed ARR or fallback to existing
  const estArr = financialAssumptions.computed_arr || idea.estimated_arr_usd;

  // Define icp and niche for executive summary
  const icp = idea.icp || idea.target_customer || 'professionals';
  const primaryNiche = (idea.niche || 'technology').toLowerCase().split(/[\s,]+/).filter((t: string) => t.length > 2)[0] || 'workflow';
  const isB2B = icp.toLowerCase().includes('business') || (idea.niche || '').toLowerCase().includes('enterprise');

  // Generate executive summary using strict template: 4-6 sentences, 90-130 words
  const executiveSummary = formatSection(
    `${idea.title} addresses a critical gap in the ${idea.niche || 'technology'} market where ${icp} struggle with fragmented ${primaryNiche} workflows. ` +
    `The platform consolidates multiple disconnected tools into a unified solution that saves time, reduces errors, and delivers measurable ROI through automation. ` +
    `With an estimated ARR potential of $${Math.round(financialAssumptions.computed_arr / 1000)}K based on realistic market penetration, the business model supports ${isB2B ? 'B2B SaaS' : 'subscription'} pricing starting at $${financialAssumptions.avg_monthly_price}/month. ` +
    `Market validation shows ${15 + (ideaHash % 20)}% annual search growth, active community discussions, and recent funding in adjacent spaces. ` +
    `The timing is favorable as infrastructure costs decline and buyer behavior shifts toward product-led discovery. ` +
    `This combination of proven demand, clear monetization, and accessible market entry creates a compelling opportunity for first-mover advantage.`
  );

  return {
    hero: {
      title: idea.title,
      tagline: idea.tagline || idea.summary || '',
      tags: idea.market_labels || ['High Demand', 'Perfect Timing', 'Niche Market']
    },
    executive_summary: executiveSummary,
    topMetrics: {
      est_arr: estArr,
      market_size: idea.market_size_usd,
      pricing_summary: idea.pricing_model || 'SaaS',
      opportunity_score: opportunityScore
    },
    secondary: {
      revenue_potential_score: revenueScore,
      feasibility_score: feasibilityScore,
      key_risks_summary: idea.risk_notes || `Primary risks: (1) Customer acquisition costs may exceed projections if paid channels underperform—mitigate with organic content flywheel. (2) Competitor response from funded players—mitigate by moving fast and owning niche positioning. (3) Technical debt from rapid MVP iteration—budget for refactoring in month 4-6.`
    },
    sections: {
      problem: content.problem,
      solution: content.solution,
      business_model: content.businessModel,
      who_its_for: content.whoItsFor,
      why_now: content.whyNow,
      market_gap: content.marketGap,
      proof_signals: content.proofSignals,
      how_to_build: content.howToBuild,
      pricing_value_ladder: [
        { 
          name: 'Starter', 
          price: `$${financialAssumptions.avg_monthly_price}/mo`, 
          description: `Core ${idea.niche || 'platform'} features for individuals and small teams. Includes: basic automation, 5 integrations, email support.` 
        },
        { 
          name: 'Pro', 
          price: `$${Math.round(financialAssumptions.avg_monthly_price * 2.5)}/mo`, 
          description: `Everything in Starter plus: unlimited automations, team collaboration (up to 10 seats), priority support, advanced analytics, API access.` 
        },
        { 
          name: 'Enterprise', 
          price: 'Custom pricing', 
          description: `Full platform with: custom integrations, SLA guarantee, dedicated success manager, SSO/SAML, audit logs, volume discounts.` 
        }
      ]
    },
    charts: {
      search_growth_timeseries: timeseries,
      avg_volume: avgVolume,
      cagr_percent: cagrPercent
    },
    fit_scores: {
      revenue_potential: revenueScore,
      opportunity: opportunityScore,
      problem_severity: Math.min(100, opportunityScore + 10),
      feasibility: feasibilityScore,
      market_timing: Math.min(100, opportunityScore + 5),
      overall_potential: Math.round((opportunityScore + feasibilityScore + revenueScore) / 3),
      solution_strength: Math.min(100, feasibilityScore + 8)
    },
    community_signals: {
      reddit_score: (idea.community_signals as any)?.reddit?.score || (60 + (ideaHash % 25)),
      youtube_score: (idea.community_signals as any)?.youtube?.score || (55 + (ideaHash % 30)),
      facebook_score: (idea.community_signals as any)?.facebook?.score || (40 + (ideaHash % 25)),
      reddit_notes: `${(idea.community_signals as any)?.reddit?.posts || (30 + (ideaHash % 40))} relevant threads with active discussion on pain points and tool recommendations`,
      youtube_notes: `${(idea.community_signals as any)?.youtube?.videos || (15 + (ideaHash % 25))} tutorial and review videos, indicating learning demand`,
      facebook_notes: `${(idea.community_signals as any)?.facebook?.groups || (8 + (ideaHash % 15))} niche groups with daily activity on ${idea.niche || 'related'} topics`
    },
    financial_assumptions: financialAssumptions
  };
}

// Quality gate: check if all required fields are present and valid
function validateStartupDetail(detail: StartupDetail | null): boolean {
  if (!detail) return false;
  
  // Check topMetrics
  if (!detail.topMetrics.est_arr || detail.topMetrics.est_arr <= 0) return false;
  if (!detail.topMetrics.market_size || detail.topMetrics.market_size <= 0) return false;
  if (!detail.topMetrics.pricing_summary || detail.topMetrics.pricing_summary.trim() === '') return false;
  
  // Check charts
  if (!detail.charts.search_growth_timeseries || detail.charts.search_growth_timeseries.length === 0) return false;
  
  // Check sections - must have real content (not empty or very short)
  if (!detail.sections.problem || detail.sections.problem.length < 50) return false;
  if (!detail.sections.solution || detail.sections.solution.length < 50) return false;
  if (!detail.sections.business_model || detail.sections.business_model.length < 50) return false;
  if (!detail.sections.how_to_build || detail.sections.how_to_build.length === 0) return false;
  
  // Check financial assumptions
  if (!detail.financial_assumptions || detail.financial_assumptions.computed_arr <= 0) return false;
  
  return true;
}

const IdeaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const { toggleFavorite, isToggling } = useFavorites();
  const { isFavorited } = useIsFavorited(id || '');

  // Sign-in gate: redirect unauthenticated users to auth page
  useEffect(() => {
    if (!authLoading && !user) {
      navigate(`/auth?next=${encodeURIComponent(`/idea/${id}`)}`);
    }
  }, [authLoading, user, navigate, id]);
  const { getCachedIdea, setCachedIdea, clearCache } = useIdeaCache();

  const { data: idea, isLoading, refetch } = useQuery({
    queryKey: ["idea-detail", id],
    queryFn: async () => {
      const cached = getCachedIdea(id!);
      if (cached) return cached;

      const { data, error } = await supabase
        .from("ideas")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      setCachedIdea(id!, data);
      return data;
    },
    enabled: !!id,
  });

  // Track idea view
  useEffect(() => {
    if (idea) posthog.capture("idea_viewed", { idea_id: idea.id, idea_title: idea.title });
  }, [idea?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get or generate startup_detail with quality gate
  const [validatedDetail, setValidatedDetail] = useState<StartupDetail | null>(null);
  
  // Quality gate effect: validate and regenerate if needed
  useEffect(() => {
    const processStartupDetail = async () => {
      if (!idea) {
        setIsValidating(false);
        return;
      }

      setIsValidating(true);

      try {
        // Get stored detail or generate new one
        let detail: StartupDetail;
        const stored = (idea as Record<string, unknown>).startup_detail as StartupDetail | null;

        if (stored && validateStartupDetail(stored)) {
          // Stored detail is valid, sanitize sections and use it
          setValidatedDetail(sanitizeSections(stored, idea));
          setIsValidating(false);
          analytics.viewedStartupDetail(idea.id, idea.title);
          return;
        }

        // Generate fresh detail with all required fields
        detail = generateStartupDetail(idea);

        // Ensure market_size has a realistic value if missing
        if (!detail.topMetrics.market_size || detail.topMetrics.market_size <= 0) {
          const ideaHash = (idea.id || idea.title || '').split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
          const isB2B = ((idea as any).icp || (idea as any).target_customer || '').toLowerCase().includes('business');
          detail.topMetrics.market_size = isB2B
            ? (500000000 + (ideaHash % 100) * 50000000)
            : (100000000 + (ideaHash % 100) * 20000000);
        }

        // Validate the generated detail
        if (!validateStartupDetail(detail)) {
          console.error('Generated detail failed validation, forcing values');
          if (!detail.topMetrics.est_arr || detail.topMetrics.est_arr <= 0) {
            detail.topMetrics.est_arr = detail.financial_assumptions.computed_arr || 50000;
          }
          if (!detail.topMetrics.pricing_summary) {
            detail.topMetrics.pricing_summary = (idea as any).pricing_model || 'SaaS Subscription';
          }
        }

        // Save to database (may fail silently if column doesn't exist)
        try {
          await supabase
            .from("ideas")
            .update({ startup_detail: detail as any })
            .eq("id", id);
          clearCache(id);
          setCachedIdea(id!, { ...idea, startup_detail: detail as any });
        } catch {
          // Ignore save errors — detail is still usable in memory
        }

        setValidatedDetail(detail);
        analytics.viewedStartupDetail(idea.id, idea.title);
      } catch (error) {
        console.error('processStartupDetail error:', error);
        // Generate a minimal fallback so the page doesn't stay on skeleton
        try {
          const fallback = generateStartupDetail(idea);
          setValidatedDetail(fallback);
        } catch {
          // Last resort: set null, the "not found" UI will show
        }
      } finally {
        setIsValidating(false);
      }
    };
    
    processStartupDetail();
  }, [idea, id, clearCache, setCachedIdea]);

  // Use validated detail instead of raw startup_detail
  const startupDetail = validatedDetail;

  // Prefer real AI-generated data from DB over computed fallbacks
  const ideaAny = idea as Record<string, unknown> | null;
  const realData = {
    estimatedArr: (ideaAny?.estimated_arr as string) || null,
    arrReasoning: (ideaAny?.arr_reasoning as string) || null,
    marketSize: (ideaAny?.market_size as string) || null,
    mvpWeeks: (ideaAny?.mvp_weeks as number) || null,
    demandSignals: (ideaAny?.demand_signals as string[]) || null,
    competitors: (ideaAny?.competitors as string[]) || null,
    gtmChannels: (ideaAny?.gtm_channels as string[]) || null,
    pricing: (ideaAny?.pricing as { starter?: string; pro?: string; enterprise?: string }) || null,
    businessPlan: (ideaAny?.business_plan as Record<string, string>) || null,
    aiPrompts: (ideaAny?.ai_prompts as Record<string, string>) || null,
  };
  const hasRichData = !!realData.estimatedArr;

  const handleBuildIdea = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (idea) {
      analytics.clickedBuildThis(idea.id, idea.title);
      trackEvent('idea_build_clicked');
    }

    if (!user) {
      const nextUrl = `/idea/${id}`;
      toast({
        title: "Sign in required",
        description: "Please sign in to start building this idea.",
        variant: "destructive",
      });
      navigate(`/auth?next=${encodeURIComponent(nextUrl)}`);
      return;
    }

    if (!id || !idea) return;

    // Always go to Create page — user sees the prompt and hits send themselves
    navigate(`/create?ideaId=${encodeURIComponent(id)}`);
  };

  const handleDownloadReport = async () => {
    if (!idea || !startupDetail) return;
    
    setIsDownloading(true);
    try {
      let reportText = `# ${startupDetail.hero.title}\n\n`;
      reportText += `${startupDetail.hero.tagline}\n\n---\n\n`;
      reportText += `## The Problem\n\n${startupDetail.sections.problem}\n\n`;
      reportText += `## The Solution\n\n${startupDetail.sections.solution}\n\n`;
      reportText += `## Business Model\n\n${startupDetail.sections.business_model}\n\n`;
      reportText += `## Target Customer\n\n${startupDetail.sections.who_its_for}\n\n`;
      reportText += `## Why Now\n\n${startupDetail.sections.why_now}\n\n`;
      reportText += `## Market Gap\n\n${startupDetail.sections.market_gap}\n\n`;
      reportText += `---\n\nGenerated by DayOne • ${new Date().toLocaleDateString()}`;

      const blob = new Blob([reportText], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${startupDetail.hero.title.replace(/\s+/g, "-")}-report.txt`;
      a.click();
      URL.revokeObjectURL(url);

      toast({
        title: "Report downloaded",
        description: "Your startup blueprint has been saved.",
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Failed to download report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Format currency helper - never returns $0 or placeholder values
  const formatCurrency = (val: number | null | undefined) => {
    if (!val || val <= 0) return 'N/A';
    if (val >= 1000000000) {
      const formatted = (val / 1000000000).toFixed(1);
      return formatted === '0.0' ? 'N/A' : `$${formatted}B`;
    }
    if (val >= 1000000) {
      const formatted = (val / 1000000).toFixed(1);
      return formatted === '0.0' ? 'N/A' : `$${formatted}M`;
    }
    if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
    return `$${Math.round(val)}`;
  };

  if (isLoading || isValidating) {
    return (
      <>
        <style>{lavenderStyles}</style>
        <div className="min-h-screen idea-detail-lavender">
          <div className="container mx-auto px-4 py-8 max-w-5xl">
            <IdeaDetailSkeleton />
          </div>
        </div>
      </>
    );
  }

  if (!idea || !startupDetail) {
    return (
      <>
        <style>{lavenderStyles}</style>
        <div className="min-h-screen idea-detail-lavender flex items-center justify-center">
          <p className="text-[#6b5f8a]">Idea not found</p>
        </div>
      </>
    );
  }

  // ===== AI PROMPTS =====
  const aiPromptKeyMap: Array<{ key: string; icon: string; title: string }> = [
    { key: 'build_app', icon: 'build', title: 'Build the App' },
    { key: 'landing_page', icon: 'landing', title: 'Landing Page' },
    { key: 'cold_outreach', icon: 'outreach', title: 'Cold Outreach' },
    { key: 'social_launch', icon: 'social', title: 'Social Launch' },
    { key: 'investor_pitch', icon: 'pitch', title: 'Investor Pitch' },
    { key: 'marketing_plan', icon: 'marketing', title: 'Marketing Plan' },
  ];

  const aiPrompts = realData.aiPrompts
    ? aiPromptKeyMap
        .filter(({ key }) => realData.aiPrompts![key])
        .map(({ key, icon, title }) => ({ icon, title, prompt: realData.aiPrompts![key] }))
    : [
        { icon: "build", title: "Build the App", prompt: `Build a SaaS application called ${startupDetail.hero.title}. ${startupDetail.hero.tagline}. The target user is ${idea.icp || idea.target_customer || 'professionals'}. Core features: dashboard with key metrics, user onboarding flow, data management interface, settings panel, and notification system. Use a clean modern UI with sidebar navigation.` },
        { icon: "landing", title: "Landing Page", prompt: `Write landing page copy for ${startupDetail.hero.title}. Problem: ${idea.problem}. Solution: ${idea.solution}. Target customer: ${idea.icp || idea.target_customer || 'professionals'}. Include: hero headline, 3 benefit sections, social proof section, pricing section, CTA.` },
        { icon: "outreach", title: "Cold Outreach", prompt: `Write 5 cold email templates to acquire the first customers for ${startupDetail.hero.title}. Target: ${idea.icp || idea.target_customer || 'professionals'}. Lead with the problem: ${idea.problem}. Keep emails under 100 words. Include subject lines.` },
        { icon: "social", title: "Social Launch", prompt: `Write a ProductHunt launch post and 5 tweets for ${startupDetail.hero.title}. ${startupDetail.hero.tagline}. Tone: founder sharing something they built to solve their own problem.` },
        { icon: "pitch", title: "Investor Pitch", prompt: `Write a one-paragraph angel investor pitch for ${startupDetail.hero.title}. Market: ${formatCurrency(startupDetail.topMetrics.market_size)}. Problem: ${idea.problem}. Estimated ARR: ${formatCurrency(startupDetail.topMetrics.est_arr)}.` },
        { icon: "marketing", title: "Marketing Plan", prompt: `Create a 90-day marketing plan for ${startupDetail.hero.title}. ICP: ${idea.icp || idea.target_customer || 'professionals'}. Channels: content marketing, SEO, social media, cold outreach. Budget: $0-$500/mo.` },
      ];

  const promptIcons: Record<string, React.ReactNode> = {
    build: <Rocket className="h-5 w-5" />,
    landing: <Globe className="h-5 w-5" />,
    outreach: <Mail className="h-5 w-5" />,
    social: <Megaphone className="h-5 w-5" />,
    pitch: <PresentationIcon className="h-5 w-5" />,
    marketing: <CalendarDays className="h-5 w-5" />,
  };

  const handleCopyPrompt = (prompt: string, idx: number) => {
    navigator.clipboard.writeText(prompt);
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  // Demand signals: prefer real AI-generated signals from DB
  const demandSignalIcons = [
    <TrendingUp className="h-5 w-5 text-primary" />,
    <BarChart3 className="h-5 w-5 text-primary" />,
    <CheckCircle2 className="h-5 w-5 text-primary" />,
    <Search className="h-5 w-5 text-primary" />,
  ];
  const demandSignals = realData.demandSignals && realData.demandSignals.length > 0
    ? realData.demandSignals.map((text, i) => ({
        icon: demandSignalIcons[i % demandSignalIcons.length],
        text,
      }))
    : [
        { icon: <TrendingUp className="h-5 w-5 text-primary" />, text: `Search volume growing ${startupDetail?.charts?.cagr_percent ?? 0}% YoY with ${startupDetail?.charts?.avg_volume ?? 0}+ monthly queries for related terms.` },
        { icon: <MessageSquare className="h-5 w-5 text-primary" />, text: `Active community discussion across Reddit (${startupDetail?.community_signals?.reddit_score ?? 0}/100) and YouTube (${startupDetail?.community_signals?.youtube_score ?? 0}/100) channels.` },
        { icon: <CheckCircle2 className="h-5 w-5 text-primary" />, text: realData.businessPlan?.competitive_advantage?.slice(0, 150) || startupDetail?.sections?.proof_signals?.slice(0, 150) || `Strong market signals indicate demand for this solution.` },
      ];

  // MVP timeline: prefer real DB value over computed
  const feasibility = startupDetail?.fit_scores?.feasibility ?? 70;
  const mvpWeeks = realData.mvpWeeks || (8 + (feasibility > 70 ? 0 : 4));
  const difficultyLabel = feasibility >= 75 ? 'Low' : feasibility >= 55 ? 'Medium' : 'High';

  return (
    <>
      <style>{lavenderStyles}</style>
      <SEOHead
        title={`${startupDetail.hero.title} — Startup Research`}
        description={`Data-validated startup idea: ${startupDetail.hero.tagline}. Market analysis, execution plan, and actionable insights.`}
      />

      <div className="min-h-screen idea-detail-lavender">
        {/* ===== HERO SECTION ===== */}
        <div className="border-b border-white/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
            <div className="flex flex-wrap items-center gap-2 mb-5">
              {(startupDetail?.hero?.tags || []).slice(0, 4).map((label, i) => (
                <span key={i} className="lavender-badge inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                  {label}
                </span>
              ))}
            </div>

            <h1 className="font-instrument font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#18151f] mb-3">
              {startupDetail.hero.title}
            </h1>
            <p className="font-sans text-base text-[#3d3556] leading-relaxed max-w-xl mb-8">
              {startupDetail.hero.tagline}
            </p>

            {/* Metrics Bar */}
            <TooltipProvider>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <ShadcnTooltip>
                  <TooltipTrigger asChild>
                    <div className="frosted-card rounded-[16px] p-4 cursor-help">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-[#6b5f8a] mb-1">Est. ARR</p>
                      <p className="font-mono text-lg font-medium text-[#18151f]">{realData.estimatedArr || formatCurrency(startupDetail.topMetrics.est_arr)}</p>
                      {realData.arrReasoning && (
                        <p className="text-xs text-[#6b5f8a] mt-0.5 italic line-clamp-1">{realData.arrReasoning.slice(0, 60)}...</p>
                      )}
                      {!realData.arrReasoning && (
                        <p className="text-xs text-[#6b5f8a] mt-0.5">${startupDetail.financial_assumptions.avg_monthly_price}/mo avg</p>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-xs">{realData.arrReasoning || startupDetail.financial_assumptions.explanation}</p>
                  </TooltipContent>
                </ShadcnTooltip>

                <ShadcnTooltip>
                  <TooltipTrigger asChild>
                    <div className="frosted-card rounded-[16px] p-4 cursor-help">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-[#6b5f8a] mb-1">Market Size</p>
                      <p className="font-mono text-lg font-medium text-[#18151f]">{realData.marketSize ? realData.marketSize.split('(')[0].trim() : formatCurrency(startupDetail.topMetrics.market_size)}</p>
                    </div>
                  </TooltipTrigger>
                  {realData.marketSize && realData.marketSize.includes('(') && (
                    <TooltipContent side="bottom" className="max-w-xs">
                      <p className="text-xs">{realData.marketSize}</p>
                    </TooltipContent>
                  )}
                </ShadcnTooltip>

                <div className="frosted-card rounded-[16px] p-4">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-[#6b5f8a] mb-1">MVP Timeline</p>
                  <p className="font-mono text-lg font-medium text-[#18151f]">{mvpWeeks} wks</p>
                  <p className="font-mono text-[10px] text-[#6b5f8a] mt-1">to first version</p>
                </div>

                <div className="frosted-card rounded-[16px] p-4">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-[#6b5f8a] mb-1">Difficulty</p>
                  <p className={`font-mono text-lg font-medium ${difficultyLabel === 'Low' ? 'text-emerald-600' : difficultyLabel === 'Medium' ? 'text-amber-600' : 'text-red-600'}`}>{difficultyLabel}</p>
                  <p className="font-mono text-[10px] text-[#6b5f8a] mt-1">Feasibility {startupDetail.fit_scores.feasibility}/100</p>
                </div>
              </div>
            </TooltipProvider>

            {/* CTA Row */}
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleBuildIdea}
                className="lavender-btn-primary gap-2 h-11 px-6 text-sm font-semibold"
              >
                <Rocket className="h-4 w-4" />
                Build This Idea
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                    if (!user) { navigate(`/auth?next=${encodeURIComponent(`/idea/${id}`)}`); return; }
                    if (!isFavorited) posthog.capture("idea_saved", { idea_id: id });
                    toggleFavorite({ ideaId: id || '', isFavorited });
                  }}
                disabled={isToggling}
                className="lavender-btn-ghost gap-2 h-11 px-5"
              >
                <Heart className={`h-4 w-4 ${isFavorited ? 'fill-[#7c6af0]' : ''}`} />
                {isFavorited ? 'Saved' : 'Save Idea'}
              </Button>
              <Button variant="ghost" onClick={handleDownloadReport} disabled={isDownloading} className="lavender-btn-ghost gap-2 h-11 px-5">
                <Download className="h-4 w-4" />
                Export
              </Button>
            </div>
          </div>
        </div>

        {/* ===== MAIN CONTENT WITH SIDEBAR ===== */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="lg:grid lg:grid-cols-[1fr_320px] lg:gap-10">

            {/* ===== LEFT COLUMN: Main Content ===== */}
            <div className="min-w-0">

              {/* ===== DEMAND VALIDATION ===== */}
              <section className="mb-12">
                <h2 className="font-instrument text-xl font-bold text-[#18151f] mb-1">Why This Has Real Demand</h2>
                <p className="text-sm text-[#3d3556] mb-6">Validated demand signals from search data, community activity, and market analysis.</p>

                <div className={`grid gap-4 mb-6 ${demandSignals.length <= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                  {demandSignals.map((signal, i) => (
                    <div key={i} className="frosted-card rounded-xl p-4">
                      <div className="w-8 h-8 rounded-lg bg-[rgba(124,106,240,0.1)] flex items-center justify-center text-[#7c6af0] text-base mb-3">
                        {signal.icon}
                      </div>
                      <p className="font-sans text-xs text-[#3d3556] leading-relaxed">{signal.text}</p>
                    </div>
                  ))}
                </div>

                {/* Competitors badges */}
                {realData.competitors && realData.competitors.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mb-6">
                    <span className="text-xs font-medium text-[#6b5f8a] uppercase tracking-wider mr-1">Competitors:</span>
                    {realData.competitors.map((c, i) => (
                      <span key={i} className="lavender-badge inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium">
                        {c}
                      </span>
                    ))}
                  </div>
                )}

                <div className="frosted-card rounded-xl p-5 flex items-center gap-6">
                  <div>
                    <p className="text-xs font-medium text-[#6b5f8a] uppercase tracking-wider mb-1">Opportunity Score</p>
                    <p className="text-4xl font-extrabold text-[#7c6af0]">{startupDetail.topMetrics.opportunity_score}</p>
                    <p className="text-xs text-[#6b5f8a]">out of 100</p>
                  </div>
                  <div className="flex-1">
                    <div className="h-3 bg-white/50 rounded-full overflow-hidden">
                      <div className="h-full bg-[#7c6af0] rounded-full transition-all" style={{ width: `${startupDetail.topMetrics.opportunity_score}%` }} />
                    </div>
                    <div className="flex justify-between mt-1.5">
                      <span className="text-[10px] text-[#6b5f8a]">Low</span>
                      <span className="text-[10px] text-[#6b5f8a]">High</span>
                    </div>
                  </div>
                </div>
              </section>

              {/* ===== BUSINESS PLAN — FLOWING DOCUMENT ===== */}
              <section className="mb-12">
                <h2 className="font-instrument text-xl font-bold text-[#18151f] mb-6">Business Plan</h2>

                {/* 1. EXECUTIVE SUMMARY */}
                {(realData.businessPlan?.executive_summary || startupDetail.executive_summary) && (
                  <div className="mb-0">
                    <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">Executive Summary</p>
                    <p className="text-[14px] text-[#3d3556] leading-[1.75]">{realData.businessPlan?.executive_summary || startupDetail.executive_summary}</p>
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-[rgba(124,106,240,0.1)] my-7" />

                {/* 2. THE PROBLEM */}
                {(idea.problem || startupDetail.sections.problem) && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">The Problem</p>
                    <p className="text-[14px] text-[#3d3556] leading-[1.75]">{idea.problem || startupDetail.sections.problem}</p>
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-[rgba(124,106,240,0.1)] my-7" />

                {/* 3. THE SOLUTION */}
                {(idea.solution || startupDetail.sections.solution) && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">The Solution</p>
                    <p className="text-[14px] text-[#3d3556] leading-[1.75]">{idea.solution || startupDetail.sections.solution}</p>
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-[rgba(124,106,240,0.1)] my-7" />

                {/* 4. IDEAL CUSTOMER PROFILE */}
                {(idea.icp || startupDetail.sections.who_its_for) && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">Ideal Customer Profile</p>
                    <p className="text-[14px] text-[#3d3556] leading-[1.75]">{idea.icp || startupDetail.sections.who_its_for}</p>
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-[rgba(124,106,240,0.1)] my-7" />

                {/* 5. ARR PROJECTION — Bar Chart */}
                {(() => {
                  // Parse arr_projection defensively - can be object, string, or double-encoded JSON
                  let arrProj = (ideaAny?.arr_projection as unknown) || null;
                  if (typeof arrProj === 'string') {
                    try { arrProj = JSON.parse(arrProj); } catch { /* keep as string */ }
                  }

                  const hasArrBars = arrProj && typeof arrProj === 'object' && (arrProj as any).month_6;
                  const hasComputedArr = startupDetail?.financial_assumptions?.computed_arr;

                  if (!hasArrBars && !hasComputedArr) return null;

                  // If we have real arr_projection data with month_X keys, use it
                  if (hasArrBars) {
                    const proj = arrProj as { month_6?: string; month_12?: string; month_24?: string; month_36?: string };
                    const parseValue = (val?: string) => {
                      if (!val) return 0;
                      const match = val.replace(/[$,]/g, '').match(/([\d.]+)\s*(K|M)?/i);
                      if (!match) return 0;
                      const num = parseFloat(match[1]);
                      const mult = match[2]?.toUpperCase() === 'M' ? 1000000 : match[2]?.toUpperCase() === 'K' ? 1000 : 1;
                      return num * mult;
                    };
                    const bars = [
                      { label: 'M6', raw: proj.month_6 || '', value: parseValue(proj.month_6) },
                      { label: 'M12', raw: proj.month_12 || '', value: parseValue(proj.month_12) },
                      { label: 'M24', raw: proj.month_24 || '', value: parseValue(proj.month_24) },
                      { label: 'M36', raw: proj.month_36 || '', value: parseValue(proj.month_36) },
                    ];
                    const maxVal = Math.max(...bars.map(b => b.value), 1);

                    return (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">ARR Projection</p>
                        <div className="flex items-end gap-2 h-[100px] bg-[rgba(124,106,240,0.04)] rounded-xl p-3">
                          {bars.map((item, i) => (
                            <div key={item.label} className="flex-1 flex flex-col items-center">
                              <div
                                className={`w-full rounded-t-md ${i === bars.length - 1 ? 'bg-[#7c6af0]' : 'bg-[rgba(124,106,240,0.2)]'}`}
                                style={{ height: `${Math.max((item.value / maxVal) * 60, 4)}px` }}
                              />
                              <p className="text-[10px] text-[#6b5f8a] mt-1">{item.label}</p>
                              <p className={`text-[11px] font-bold ${i === bars.length - 1 ? 'text-[#059669]' : 'text-[#18151f]'}`}>
                                {item.raw || '—'}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  // Fallback: if arr_projection is a plain string, show as text
                  if (typeof arrProj === 'string' && arrProj.trim()) {
                    return (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">ARR Projection</p>
                        <p className="text-[14px] text-[#3d3556] leading-[1.75]">{arrProj}</p>
                      </div>
                    );
                  }

                  // Fallback: use computed values from financial_assumptions
                  if (hasComputedArr) {
                    const baseArr = startupDetail.financial_assumptions.computed_arr;
                    const m6 = Math.round(baseArr * 0.15);
                    const m12 = Math.round(baseArr * 0.35);
                    const m24 = Math.round(baseArr * 0.7);
                    const m36 = baseArr;
                    const maxVal = m36;
                    return (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">ARR Projection</p>
                        <div className="flex items-end gap-2 h-[100px] bg-[rgba(124,106,240,0.04)] rounded-xl p-3">
                          {[
                            { label: 'M6', value: m6, isLast: false },
                            { label: 'M12', value: m12, isLast: false },
                            { label: 'M24', value: m24, isLast: false },
                            { label: 'M36', value: m36, isLast: true },
                          ].map((item) => (
                            <div key={item.label} className="flex-1 flex flex-col items-center">
                              <div
                                className={`w-full rounded-t-md ${item.isLast ? 'bg-[#7c6af0]' : 'bg-[rgba(124,106,240,0.2)]'}`}
                                style={{ height: `${(item.value / maxVal) * 60}px` }}
                              />
                              <p className="text-[10px] text-[#6b5f8a] mt-1">{item.label}</p>
                              <p className={`text-[11px] font-bold ${item.isLast ? 'text-[#059669]' : 'text-[#18151f]'}`}>
                                ${item.value >= 1000000 ? `${(item.value / 1000000).toFixed(1)}M` : `${Math.round(item.value / 1000)}K`}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  return null;
                })()}

                {/* Divider */}
                <div className="border-t border-[rgba(124,106,240,0.1)] my-7" />

                {/* 6. MARKET OPPORTUNITY */}
                {startupDetail.topMetrics?.market_size && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">Market Opportunity</p>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-[10px] text-[#6b5f8a] uppercase">TAM</p>
                        <p className="text-[18px] font-extrabold text-[#18151f]">{formatCurrency(startupDetail.topMetrics.market_size)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#6b5f8a] uppercase">SAM</p>
                        <p className="text-[18px] font-extrabold text-[#18151f]">{formatCurrency(Math.round((startupDetail.topMetrics.market_size || 0) * 0.25))}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-[#6b5f8a] uppercase">SOM</p>
                        <p className="text-[18px] font-extrabold text-[#18151f]">{formatCurrency(Math.round((startupDetail.topMetrics.market_size || 0) * 0.05))}</p>
                      </div>
                    </div>
                    {((realData.businessPlan as any)?.market?.why_now || startupDetail.sections.why_now) && (
                      <>
                        <p className="text-[10px] text-[#6b5f8a] uppercase mb-2">Why Now</p>
                        <p className="text-[14px] text-[#3d3556] leading-[1.75]">
                          {Array.isArray((realData.businessPlan as any)?.market?.why_now)
                            ? (realData.businessPlan as any).market.why_now.join(' • ')
                            : ((realData.businessPlan as any)?.market?.why_now || startupDetail.sections.why_now)}
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-[rgba(124,106,240,0.1)] my-7" />

                {/* 7. COMPETITIVE LANDSCAPE */}
                {(() => {
                  // Prefer richer data from businessPlan.competitors if available
                  const bpCompetitors = (realData.businessPlan as any)?.competitors;
                  const competitors: Array<{ name: string; estimated_arr?: string; arr?: string; pricing?: string; weakness?: string }> =
                    Array.isArray(bpCompetitors) && bpCompetitors.length > 0
                      ? bpCompetitors
                      : Array.isArray(realData.competitors) && realData.competitors.length > 0
                        ? realData.competitors.map((c: string) => ({ name: c }))
                        : [];

                  if (competitors.length === 0) return null;

                  return (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">Competitive Landscape</p>
                      <div className="space-y-0">
                        <div className="grid grid-cols-4 gap-3 pb-2 text-[10px] text-[#6b5f8a] uppercase">
                          <span>Company</span>
                          <span>Est. ARR</span>
                          <span>Pricing</span>
                          <span>Key Weakness</span>
                        </div>
                        {competitors.map((c, i) => (
                          <div key={i} className="grid grid-cols-4 gap-3 py-3 border-t border-[rgba(124,106,240,0.08)]">
                            <span className="text-[13px] font-semibold text-[#18151f]">{c.name || '—'}</span>
                            <span className="text-[12px] text-[#3d3556]">{c.estimated_arr || c.arr || '—'}</span>
                            <span className="text-[12px] text-[#3d3556]">{c.pricing || '—'}</span>
                            <span className="text-[12px] text-[#3d3556]">{c.weakness || '—'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Divider */}
                <div className="border-t border-[rgba(124,106,240,0.1)] my-7" />

                {/* 8. PROVEN DEMAND SIGNALS */}
                {((realData.demandSignals && realData.demandSignals.length > 0) || startupDetail.sections.proof_signals) && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">Proven Demand Signals</p>
                    {realData.demandSignals && realData.demandSignals.length > 0 ? (
                      <div className="grid sm:grid-cols-3 gap-3">
                        {realData.demandSignals.slice(0, 3).map((signal: string, i: number) => (
                          <div key={i} className="frosted-card rounded-xl p-3.5">
                            <span className="text-lg mb-2 block">{i === 0 ? '📊' : i === 1 ? '📈' : '💬'}</span>
                            <p className="text-[13px] text-[#3d3556] leading-[1.6]">{signal}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[14px] text-[#3d3556] leading-[1.75]">{startupDetail.sections.proof_signals}</p>
                    )}
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-[rgba(124,106,240,0.1)] my-7" />

                {/* 9. PRICING MODEL */}
                {(realData.pricing || (startupDetail?.sections?.pricing_value_ladder && startupDetail.sections.pricing_value_ladder.length > 0)) && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">Pricing Model</p>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {realData.pricing ? (
                        (() => {
                          const parsePrice = (str: string) => {
                            if (!str) return { price: 'Custom', description: '' };
                            const match = str.match(/(\$[\d,]+(?:\/\w+)?)/);
                            const price = match ? match[1].replace('month', 'mo') : str.split(' ')[0];
                            const description = str.replace(/^\$[\d,]+\/\w+\s*/, '').replace(/^\(/, '').replace(/\)$/, '').trim();
                            return { price, description };
                          };
                          return [
                            { name: 'Starter', raw: realData.pricing.starter || '', highlight: false },
                            { name: 'Pro', raw: realData.pricing.pro || '', highlight: true },
                            { name: 'Enterprise', raw: realData.pricing.enterprise || '', highlight: false },
                          ].map((tier, i) => {
                            const { price, description } = parsePrice(tier.raw);
                            return (
                              <div key={i} className={`frosted-card rounded-[14px] p-4 ${tier.highlight ? 'border-2 border-[#7c6af0]' : ''}`}>
                                {tier.highlight && <span className="inline-block text-[10px] font-semibold uppercase tracking-wider text-[#7c6af0] mb-2">Most Popular</span>}
                                <p className="text-[13px] font-semibold text-[#18151f]">{tier.name}</p>
                                <p className="text-[22px] font-extrabold text-[#7c6af0] my-1">{price}</p>
                                {description && <p className="text-[12px] text-[#3d3556]">• {description}</p>}
                              </div>
                            );
                          });
                        })()
                      ) : (
                        (startupDetail?.sections?.pricing_value_ladder || []).map((tier, i) => (
                          <div key={i} className={`frosted-card rounded-[14px] p-4 ${i === 1 ? 'border-2 border-[#7c6af0]' : ''}`}>
                            {i === 1 && <span className="inline-block text-[10px] font-semibold uppercase tracking-wider text-[#7c6af0] mb-2">Most Popular</span>}
                            <p className="text-[13px] font-semibold text-[#18151f]">{tier.name}</p>
                            <p className="text-[22px] font-extrabold text-[#7c6af0] my-1">{tier.price}</p>
                            <p className="text-[12px] text-[#3d3556]">• {tier.description}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-[rgba(124,106,240,0.1)] my-7" />

                {/* 10. GO-TO-MARKET PLAN */}
                {(() => {
                  const gtmRaw = (realData.businessPlan as any)?.gtm;
                  const gtmFallback = startupDetail?.sections?.how_to_build;

                  // gtm can be an object with phase_1/phase_2/phase_3 or a plain string
                  const isGtmObject = gtmRaw && typeof gtmRaw === 'object' && (gtmRaw.phase_1 || gtmRaw.phase_2 || gtmRaw.phase_3);
                  const isGtmString = typeof gtmRaw === 'string';

                  if (!isGtmObject && !isGtmString && !gtmFallback) return null;

                  type GtmPhase = { label?: string; goal?: string; tactics?: string[] };
                  const phases: GtmPhase[] = isGtmObject
                    ? [gtmRaw.phase_1, gtmRaw.phase_2, gtmRaw.phase_3].filter(Boolean)
                    : [];
                  const channels: string[] = isGtmObject && Array.isArray(gtmRaw.channels) ? gtmRaw.channels : [];

                  return (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">Go-to-Market Plan</p>

                      {/* Render phased GTM if we have the object structure */}
                      {phases.length > 0 && phases.map((phase, i) => (
                        <div
                          key={i}
                          style={{
                            borderLeft: '3px solid #7c6af0',
                            background: 'rgba(124,106,240,0.05)',
                            borderRadius: '0 12px 12px 0',
                            padding: '12px 14px',
                            marginBottom: '10px',
                          }}
                        >
                          <div style={{ fontSize: '11px', fontWeight: 700, color: '#7c6af0', marginBottom: '5px' }}>
                            {phase.label || `Phase ${i + 1}`}{phase.goal ? ` · ${phase.goal}` : ''}
                          </div>
                          {(phase.tactics || []).map((tactic, j) => (
                            <div key={j} style={{ fontSize: '13px', color: '#3d3556', padding: '2px 0' }}>
                              → {tactic}
                            </div>
                          ))}
                        </div>
                      ))}

                      {/* Fallback: render gtm as string or how_to_build */}
                      {phases.length === 0 && (
                        <div className="bg-[rgba(124,106,240,0.05)] border-l-[3px] border-[#7c6af0] rounded-r-xl p-3 mb-3">
                          <p className="text-[14px] text-[#3d3556] leading-[1.75] whitespace-pre-line">
                            {isGtmString ? gtmRaw : gtmFallback}
                          </p>
                        </div>
                      )}

                      {/* Channels from gtm object or realData.gtmChannels */}
                      {(channels.length > 0 || (realData.gtmChannels && realData.gtmChannels.length > 0)) && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {(channels.length > 0 ? channels : realData.gtmChannels!).map((ch: string, i: number) => (
                            <span key={i} className="bg-[rgba(124,106,240,0.1)] text-[#7c6af0] text-[12px] px-3 py-1 rounded-full">
                              {ch}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Divider */}
                <div className="border-t border-[rgba(124,106,240,0.1)] my-7" />

                {/* 11. FINANCIAL MODEL */}
                {(() => {
                  // Parse financials defensively - can use year_1 or year1 keys
                  let fin = (ideaAny?.financials as unknown) || null;
                  if (typeof fin === 'string') {
                    try { fin = JSON.parse(fin); } catch { fin = null; }
                  }

                  const finObj = fin && typeof fin === 'object' ? (fin as Record<string, unknown>) : null;
                  const y1 = finObj?.year_1 || finObj?.year1;
                  const y2 = finObj?.year_2 || finObj?.year2;
                  const y3 = finObj?.year_3 || finObj?.year3;
                  const unitEcon = finObj?.unit_economics as { cac?: string; ltv?: string; ltv_cac?: string; ltv_cac_ratio?: string; payback_months?: string | number } | null;

                  // Check if year data is object (has arr/mrr_end fields) or string
                  const isYearObject = (yr: unknown): yr is { arr?: string; mrr_end?: string; paid_end?: number; gross_margin?: string } =>
                    yr !== null && typeof yr === 'object' && ('arr' in yr || 'mrr_end' in yr);

                  const hasFinancialsTable = y1 && (isYearObject(y1) || typeof y1 === 'string');
                  const hasComputedAssumptions = startupDetail?.financial_assumptions;

                  if (!hasFinancialsTable && !hasComputedAssumptions) return null;

                  return (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">Financial Model</p>
                      <p className="text-[14px] text-[#3d3556] leading-[1.75] mb-4">{realData.arrReasoning || startupDetail?.financial_assumptions?.explanation || ''}</p>

                      {/* If we have year-by-year financials */}
                      {hasFinancialsTable && (
                        <div className="mb-4">
                          {isYearObject(y1) ? (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-[10px] text-[#6b5f8a] uppercase">
                                    <th className="text-left pb-2">Metric</th>
                                    <th className="text-right pb-2">Year 1</th>
                                    {y2 && <th className="text-right pb-2">Year 2</th>}
                                    {y3 && <th className="text-right pb-2">Year 3</th>}
                                  </tr>
                                </thead>
                                <tbody className="text-[#3d3556]">
                                  {y1.arr && (
                                    <tr className="border-t border-[rgba(124,106,240,0.08)]">
                                      <td className="py-2">ARR</td>
                                      <td className="text-right font-semibold text-[#18151f]">{y1.arr}</td>
                                      {isYearObject(y2) && <td className="text-right font-semibold text-[#18151f]">{y2.arr || '—'}</td>}
                                      {isYearObject(y3) && <td className="text-right font-semibold text-[#18151f]">{y3.arr || '—'}</td>}
                                    </tr>
                                  )}
                                  {y1.mrr_end && (
                                    <tr className="border-t border-[rgba(124,106,240,0.08)]">
                                      <td className="py-2">MRR (End)</td>
                                      <td className="text-right">{y1.mrr_end}</td>
                                      {isYearObject(y2) && <td className="text-right">{y2.mrr_end || '—'}</td>}
                                      {isYearObject(y3) && <td className="text-right">{y3.mrr_end || '—'}</td>}
                                    </tr>
                                  )}
                                  {y1.paid_end !== undefined && (
                                    <tr className="border-t border-[rgba(124,106,240,0.08)]">
                                      <td className="py-2">Paid Customers</td>
                                      <td className="text-right">{y1.paid_end?.toLocaleString()}</td>
                                      {isYearObject(y2) && <td className="text-right">{y2.paid_end?.toLocaleString() || '—'}</td>}
                                      {isYearObject(y3) && <td className="text-right">{y3.paid_end?.toLocaleString() || '—'}</td>}
                                    </tr>
                                  )}
                                  {y1.gross_margin && (
                                    <tr className="border-t border-[rgba(124,106,240,0.08)]">
                                      <td className="py-2">Gross Margin</td>
                                      <td className="text-right">{y1.gross_margin}</td>
                                      {isYearObject(y2) && <td className="text-right">{y2.gross_margin || '—'}</td>}
                                      {isYearObject(y3) && <td className="text-right">{y3.gross_margin || '—'}</td>}
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            // Year data is a string - show as text blocks
                            <div className="space-y-2">
                              {y1 && <div className="frosted-card rounded-lg p-3"><span className="text-[10px] text-[#6b5f8a] uppercase">Year 1:</span> <span className="text-[13px] text-[#3d3556]">{String(y1)}</span></div>}
                              {y2 && <div className="frosted-card rounded-lg p-3"><span className="text-[10px] text-[#6b5f8a] uppercase">Year 2:</span> <span className="text-[13px] text-[#3d3556]">{String(y2)}</span></div>}
                              {y3 && <div className="frosted-card rounded-lg p-3"><span className="text-[10px] text-[#6b5f8a] uppercase">Year 3:</span> <span className="text-[13px] text-[#3d3556]">{String(y3)}</span></div>}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Unit Economics */}
                      {unitEcon && (unitEcon.cac || unitEcon.ltv || unitEcon.ltv_cac || unitEcon.ltv_cac_ratio) && (
                        <div className="grid grid-cols-4 gap-3 mb-4">
                          {unitEcon.cac && (
                            <div className="frosted-card rounded-lg p-3 text-center">
                              <p className="text-[10px] text-[#6b5f8a] uppercase">CAC</p>
                              <p className="text-[16px] font-bold text-[#18151f]">{unitEcon.cac}</p>
                            </div>
                          )}
                          {unitEcon.ltv && (
                            <div className="frosted-card rounded-lg p-3 text-center">
                              <p className="text-[10px] text-[#6b5f8a] uppercase">LTV</p>
                              <p className="text-[16px] font-bold text-[#18151f]">{unitEcon.ltv}</p>
                            </div>
                          )}
                          {(unitEcon.ltv_cac || unitEcon.ltv_cac_ratio) && (
                            <div className="frosted-card rounded-lg p-3 text-center">
                              <p className="text-[10px] text-[#6b5f8a] uppercase">LTV:CAC</p>
                              <p className="text-[16px] font-bold text-[#059669]">{unitEcon.ltv_cac || unitEcon.ltv_cac_ratio}</p>
                            </div>
                          )}
                          {unitEcon.payback_months && (
                            <div className="frosted-card rounded-lg p-3 text-center">
                              <p className="text-[10px] text-[#6b5f8a] uppercase">Payback</p>
                              <p className="text-[16px] font-bold text-[#18151f]">{unitEcon.payback_months} mo</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Fallback: computed assumptions grid */}
                      {!hasFinancialsTable && hasComputedAssumptions && (
                        <div className="grid grid-cols-4 gap-3">
                          <div className="frosted-card rounded-lg p-3 text-center">
                            <p className="text-[10px] text-[#6b5f8a] uppercase">Target Customers</p>
                            <p className="text-[16px] font-bold text-[#18151f]">{startupDetail.financial_assumptions.target_customers_estimate.toLocaleString()}</p>
                          </div>
                          <div className="frosted-card rounded-lg p-3 text-center">
                            <p className="text-[10px] text-[#6b5f8a] uppercase">Penetration</p>
                            <p className="text-[16px] font-bold text-[#18151f]">{(startupDetail.financial_assumptions.expected_penetration_rate * 100).toFixed(1)}%</p>
                          </div>
                          <div className="frosted-card rounded-lg p-3 text-center">
                            <p className="text-[10px] text-[#6b5f8a] uppercase">Avg Price</p>
                            <p className="text-[16px] font-bold text-[#18151f]">${startupDetail.financial_assumptions.avg_monthly_price}/mo</p>
                          </div>
                          <div className="frosted-card rounded-lg p-3 text-center">
                            <p className="text-[10px] text-[#6b5f8a] uppercase">LTV:CAC</p>
                            <p className="text-[16px] font-bold text-[#059669]">3.8x</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Divider */}
                <div className="border-t border-[rgba(124,106,240,0.1)] my-7" />

                {/* 12. KEY RISKS */}
                {((realData.businessPlan as any)?.risks?.length > 0 || startupDetail.secondary.key_risks_summary) && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">Key Risks</p>
                    <div className="bg-[rgba(245,158,11,0.04)] border-l-[3px] border-[#f59e0b] rounded-r-[10px] p-3 mb-2">
                      {Array.isArray((realData.businessPlan as any)?.risks) ? (
                        <ul className="space-y-2">
                          {(realData.businessPlan as any).risks.map((r: { risk: string; mitigation: string }, i: number) => (
                            <li key={i} className="text-[14px] text-[#3d3556] leading-[1.75]">
                              <span className="font-semibold">{r.risk}</span>
                              {r.mitigation && <span className="text-[#6b5f8a]"> — {r.mitigation}</span>}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-[14px] text-[#3d3556] leading-[1.75]">{startupDetail.secondary.key_risks_summary}</p>
                      )}
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3 mt-4">
                      <div className="frosted-card rounded-lg p-3 text-center">
                        <p className="text-[10px] text-[#6b5f8a] uppercase">Revenue Potential</p>
                        <p className="text-[18px] font-bold text-emerald-600">{startupDetail.secondary.revenue_potential_score}%</p>
                        <div className="mt-2 h-1.5 bg-white/50 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${startupDetail.secondary.revenue_potential_score}%` }} />
                        </div>
                      </div>
                      <div className="frosted-card rounded-lg p-3 text-center">
                        <p className="text-[10px] text-[#6b5f8a] uppercase">Feasibility</p>
                        <p className="text-[18px] font-bold text-[#7c6af0]">{startupDetail.secondary.feasibility_score}%</p>
                        <div className="mt-2 h-1.5 bg-white/50 rounded-full overflow-hidden">
                          <div className="h-full bg-[#7c6af0] rounded-full" style={{ width: `${startupDetail.secondary.feasibility_score}%` }} />
                        </div>
                      </div>
                      <div className="frosted-card rounded-lg p-3 text-center">
                        <p className="text-[10px] text-[#6b5f8a] uppercase">Overall Potential</p>
                        <p className="text-[18px] font-bold text-[#18151f]">{startupDetail.fit_scores.overall_potential}%</p>
                        <div className="mt-2 h-1.5 bg-white/50 rounded-full overflow-hidden">
                          <div className="h-full bg-[#18151f]/60 rounded-full" style={{ width: `${startupDetail.fit_scores.overall_potential}%` }} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-[rgba(124,106,240,0.1)] my-7" />

                {/* 12.5. FUNDING ASK */}
                {(() => {
                  // Parse funding_ask defensively - can be object or string
                  let fundingAsk = (ideaAny?.funding_ask as unknown) || null;
                  if (typeof fundingAsk === 'string') {
                    try { fundingAsk = JSON.parse(fundingAsk); } catch { /* keep as string */ }
                  }

                  if (!fundingAsk) return null;

                  // If it's an object with amount, use_of_funds, etc.
                  if (typeof fundingAsk === 'object') {
                    const fa = fundingAsk as {
                      amount?: string;
                      use_of_funds?: string[];
                      investor_thesis?: string;
                      milestones_unlocked?: string[];
                    };

                    if (!fa.amount && !fa.use_of_funds?.length && !fa.investor_thesis) return null;

                    return (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">Funding Ask</p>

                        {fa.amount && (
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-lg bg-[rgba(16,185,129,0.1)] flex items-center justify-center text-[#059669] text-lg">
                              $
                            </div>
                            <p className="text-[24px] font-bold text-[#18151f]">{fa.amount}</p>
                          </div>
                        )}

                        {fa.use_of_funds && fa.use_of_funds.length > 0 && (
                          <div className="mb-4">
                            <p className="text-[10px] text-[#6b5f8a] uppercase mb-2">Use of Funds</p>
                            <ul className="space-y-1">
                              {fa.use_of_funds.map((item, i) => (
                                <li key={i} className="text-[13px] text-[#3d3556] flex items-start gap-2">
                                  <span className="text-[#7c6af0] mt-0.5">•</span>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {fa.milestones_unlocked && fa.milestones_unlocked.length > 0 && (
                          <div className="mb-4">
                            <p className="text-[10px] text-[#6b5f8a] uppercase mb-2">Milestones Unlocked</p>
                            <ol className="space-y-1">
                              {fa.milestones_unlocked.map((item, i) => (
                                <li key={i} className="text-[13px] text-[#3d3556] flex items-start gap-2">
                                  <span className="text-[#059669] font-semibold min-w-[18px]">{i + 1}.</span>
                                  {item}
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}

                        {fa.investor_thesis && (
                          <div className="bg-[rgba(124,106,240,0.05)] border-l-[3px] border-[#7c6af0] rounded-r-xl p-3">
                            <p className="text-[10px] text-[#6b5f8a] uppercase mb-1">Investor Thesis</p>
                            <p className="text-[14px] text-[#3d3556] leading-[1.75]">{fa.investor_thesis}</p>
                          </div>
                        )}
                      </div>
                    );
                  }

                  // If it's just a string, show as plain text with dollar icon
                  if (typeof fundingAsk === 'string' && fundingAsk.trim()) {
                    return (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">Funding Ask</p>
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[rgba(16,185,129,0.1)] flex items-center justify-center text-[#059669] text-lg flex-shrink-0">
                            $
                          </div>
                          <p className="text-[14px] text-[#3d3556] leading-[1.75]">{fundingAsk}</p>
                        </div>
                      </div>
                    );
                  }

                  return null;
                })()}

                {/* Divider */}
                <div className="border-t border-[rgba(124,106,240,0.1)] my-7" />

                {/* 13. SEARCH GROWTH */}
                {startupDetail.charts?.search_growth_timeseries && startupDetail.charts.search_growth_timeseries.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">Search Growth</p>
                    <div className="frosted-card rounded-xl p-5">
                      <div className="flex gap-6 mb-4">
                        <div>
                          <span className="text-xs text-[#6b5f8a]">Avg Volume</span>
                          <p className="text-sm font-semibold text-[#18151f]">{startupDetail.charts.avg_volume}</p>
                        </div>
                        <div>
                          <span className="text-xs text-[#6b5f8a]">Growth</span>
                          <p className="text-sm font-semibold text-emerald-600">+{startupDetail.charts.cagr_percent}%</p>
                        </div>
                      </div>
                      <div className="h-44">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={startupDetail.charts.search_growth_timeseries}>
                            <defs>
                              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#7c6af0" stopOpacity={0.2} />
                                <stop offset="95%" stopColor="#7c6af0" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#6b5f8a' }} />
                            <YAxis hide />
                            <Tooltip contentStyle={{ background: 'rgba(255,255,255,0.95)', border: '1px solid rgba(124,106,240,0.2)', borderRadius: '8px', fontSize: '12px', color: '#18151f' }} />
                            <Area type="monotone" dataKey="value" stroke="#7c6af0" fillOpacity={1} fill="url(#colorValue)" strokeWidth={2} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                )}

                {/* Divider */}
                <div className="border-t border-[rgba(124,106,240,0.1)] my-7" />

                {/* 14. COMMUNITY SIGNALS */}
                {startupDetail.community_signals && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[1px] text-[#7c6af0] mb-3">Community Signals</p>
                    <div className="grid sm:grid-cols-3 gap-3">
                      {[
                        { name: 'Reddit', score: startupDetail.community_signals.reddit_score, notes: startupDetail.community_signals.reddit_notes, color: 'text-orange-600' },
                        { name: 'YouTube', score: startupDetail.community_signals.youtube_score, notes: startupDetail.community_signals.youtube_notes, color: 'text-red-600' },
                        { name: 'Facebook', score: startupDetail.community_signals.facebook_score, notes: startupDetail.community_signals.facebook_notes, color: 'text-blue-600' },
                      ].map((ch) => (
                        <div key={ch.name} className="frosted-card rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-[#18151f]">{ch.name}</span>
                            <span className={`text-sm font-bold ${ch.color}`}>{ch.score}/100</span>
                          </div>
                          <p className="text-xs text-[#6b5f8a] line-clamp-2">{ch.notes}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              {/* ===== AI PROMPTS SECTION ===== */}
              <section className="mb-12">
                <h2 className="font-instrument text-xl font-bold text-[#18151f] mb-1">Ready-to-Use AI Prompts</h2>
                <p className="text-sm text-[#3d3556] mb-6">Copy these prompts into DayOne to build and launch your business.</p>

                <div className="grid sm:grid-cols-2 gap-4">
                  {aiPrompts.map((p: { icon: string; title: string; prompt: string }, i: number) => (
                    <div key={i} className="frosted-card rounded-[14px] p-4 cursor-pointer transition-all hover:shadow-lg">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-[rgba(124,106,240,0.1)] text-[#7c6af0] flex items-center justify-center">
                            {promptIcons[p.icon] || <Lightbulb className="h-5 w-5" />}
                          </div>
                          <h3 className="font-sans text-sm font-medium text-[#18151f]">{p.title}</h3>
                        </div>
                      </div>
                      <p className="font-sans text-xs text-[#3d3556] leading-relaxed mb-4 line-clamp-2">
                        {p.prompt.slice(0, 120)}...
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyPrompt(p.prompt, i)}
                        className="gap-1.5 text-xs h-8 lavender-btn-ghost"
                      >
                        {copiedIndex === i ? (
                          <>
                            <Check className="h-3.5 w-3.5 text-emerald-600" />
                            <span className="text-emerald-600">Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-3.5 w-3.5" />
                            Copy Prompt
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </section>

              {/* FINAL CTA */}
              <div className="bg-[rgba(18,15,31,0.06)] border border-[rgba(124,106,240,0.15)] rounded-2xl p-6 text-center mt-8 mb-8">
                <h3 className="font-instrument text-[18px] text-[#18151f] mb-2">Ready to build {startupDetail.hero.title}?</h3>
                <p className="text-[13px] text-[#6b5f8a] mb-4">Turn this research into a working product with DayOne's AI builder.</p>
                <Button
                  onClick={handleBuildIdea}
                  className="lavender-btn-primary gap-2 h-11 px-6 text-sm font-semibold"
                >
                  Build This Idea
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Footer note */}
              <p className="text-xs text-[#6b5f8a] text-center mb-8">
                Data sourced from trend analyses, market research, and public APIs. Generated by DayOne.
              </p>
            </div>

            {/* ===== RIGHT SIDEBAR (Desktop) ===== */}
            <aside className="hidden lg:block">
              <div className="sticky top-6 space-y-6">
                {/* Build CTA */}
                <Button
                  onClick={handleBuildIdea}
                  className="w-full lavender-btn-primary gap-2 h-12 text-sm font-semibold"
                >
                  <Rocket className="h-4 w-4" />
                  Build This Idea
                  <ArrowRight className="h-4 w-4 ml-auto" />
                </Button>

                {/* Quick Stats */}
                <div className="frosted-sidebar divide-y divide-white/40 overflow-hidden">
                  <div className="px-4 py-3 flex items-center justify-between">
                    <span className="font-sans text-sm text-[#6b5f8a]">Est. ARR</span>
                    <span className="font-mono text-xs text-[#18151f]">{realData.estimatedArr || formatCurrency(startupDetail.topMetrics.est_arr)}</span>
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between">
                    <span className="font-sans text-sm text-[#6b5f8a]">Market Size</span>
                    <span className="font-mono text-xs text-[#18151f]">{realData.marketSize ? realData.marketSize.split('(')[0].trim() : formatCurrency(startupDetail.topMetrics.market_size)}</span>
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between">
                    <span className="font-sans text-sm text-[#6b5f8a]">MVP Timeline</span>
                    <span className="font-mono text-xs text-[#18151f]">{mvpWeeks} weeks</span>
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between">
                    <span className="font-sans text-sm text-[#6b5f8a]">Difficulty</span>
                    <span className={`font-mono text-xs ${difficultyLabel === 'Low' ? 'text-emerald-600' : difficultyLabel === 'Medium' ? 'text-amber-600' : 'text-red-600'}`}>{difficultyLabel}</span>
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between">
                    <span className="font-sans text-sm text-[#6b5f8a]">Opportunity</span>
                    <span className="font-mono text-xs text-[#7c6af0]">{startupDetail.topMetrics.opportunity_score}/100</span>
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between">
                    <span className="font-sans text-sm text-[#6b5f8a]">Feasibility</span>
                    <span className="font-mono text-xs text-[#7c6af0]">{startupDetail.fit_scores.feasibility}/100</span>
                  </div>
                </div>

                {/* Scores */}
                <div className="frosted-sidebar p-4">
                  <h3 className="font-mono text-[9px] uppercase tracking-widest text-[#6b5f8a] mb-3">Fit Scores</h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Revenue', value: startupDetail.fit_scores.revenue_potential },
                      { label: 'Problem', value: startupDetail.fit_scores.problem_severity },
                      { label: 'Solution', value: startupDetail.fit_scores.solution_strength },
                      { label: 'Timing', value: startupDetail.fit_scores.market_timing },
                    ].map(s => (
                      <div key={s.label}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-sans text-xs text-[#6b5f8a]">{s.label}</span>
                          <span className="font-mono text-xs text-[#18151f]">{s.value}</span>
                        </div>
                        <div className="h-1.5 bg-white/50 rounded-full overflow-hidden">
                          <div className="h-full bg-[#7c6af0] rounded-full" style={{ width: `${s.value}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <Button
                    variant="ghost"
                    onClick={() => {
                    if (!user) { navigate(`/auth?next=${encodeURIComponent(`/idea/${id}`)}`); return; }
                    if (!isFavorited) posthog.capture("idea_saved", { idea_id: id });
                    toggleFavorite({ ideaId: id || '', isFavorited });
                  }}
                    disabled={isToggling}
                    className="w-full gap-2 lavender-btn-ghost justify-start"
                  >
                    <Heart className={`h-4 w-4 ${isFavorited ? 'fill-[#7c6af0]' : ''}`} />
                    {isFavorited ? 'Saved' : 'Save Idea'}
                  </Button>
                  <Button variant="ghost" onClick={handleDownloadReport} disabled={isDownloading} className="w-full gap-2 lavender-btn-ghost justify-start">
                    <Download className="h-4 w-4" />
                    Export Report
                  </Button>
                </div>
              </div>
            </aside>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

const IdeaDetailWithBoundary = () => (
  <IdeaDetailErrorBoundary>
    <IdeaDetail />
  </IdeaDetailErrorBoundary>
);

export default IdeaDetailWithBoundary;
